import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getWatchBrands,
  stripReferenceNameMarkup,
  type StripReferenceNameMarkupResult,
} from '../../services/firebase';
import type { WatchBrand } from '../../types';
import { BrandMultiSelect } from '../CuratedWatches/BrandMultiSelect';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { CollapsibleSection } from './CollapsibleSection';

const code = (text: string) => <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">{text}</code>;

/**
 * Data Maintenance → Tools → Strip markup from reference names.
 *
 * Some shop feeds wrap product titles in theme tags, and references imported
 * before the import path cleaned them carry those tags in their model,
 * collection, product name, series and nickname. This drives the
 * `stripReferenceNameMarkup` callable: dry run → apply.
 *
 * Apply only ever sends the token of the report on screen, with the brand
 * filter that report was run for — the callable refuses it if either moved.
 */
export function StripReferenceNameMarkupSection() {
  // Scope: either a picked list of brands, or — explicitly — every brand.
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [allBrands, setAllBrands] = useState(false);
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [running, setRunning] = useState<null | 'dryRun' | 'apply'>(null);
  const [report, setReport] = useState<StripReferenceNameMarkupResult | null>(null);
  const [confirming, setConfirming] = useState(false);

  // The brand list is only read when someone opens the picker — this panel
  // mounts with the whole Configuration page, and most visits never use it.
  const loadBrands = useCallback(async () => {
    setBrandsLoading(true);
    try {
      const list = await getWatchBrands();
      setBrands(
        [...list].sort((a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name)),
      );
    } catch (err) {
      console.error('StripReferenceNameMarkupSection: brand list failed to load', err);
      toast.error('Could not load the brand list');
    } finally {
      setBrandsLoading(false);
    }
  }, []);

  // What the callable is sent: no filter for "all brands", else the picked ids.
  const brandIds = allBrands ? [] : selectedBrandIds;
  const scopeChosen = allBrands || selectedBrandIds.length > 0;
  // The report on screen is only actionable for the scope it was run with.
  const scopeMatchesReport =
    !!report && report.brandIds.join(',') === [...brandIds].sort().join(',');
  const canApply =
    !!report?.confirmationToken && report.mode === 'dryRun' && report.affected > 0 && scopeMatchesReport;

  const run = async (mode: 'dryRun' | 'apply') => {
    setRunning(mode);
    setConfirming(false);
    try {
      const result = await stripReferenceNameMarkup({
        mode,
        ...(mode === 'apply' ? { brandIds: report!.brandIds, confirmationToken: report!.confirmationToken } : { brandIds }),
      });
      setReport(result);
      if (mode === 'dryRun') {
        toast.success(
          result.affected === 0
            ? 'Dry run: no reference names carry markup'
            : `Dry run: ${result.affected} reference(s), ${result.fieldsToWrite} field(s) to clean`,
        );
      } else {
        const failed = result.written?.failures.length ?? 0;
        (failed ? toast.error : toast.success)(
          `Cleaned ${result.written?.references ?? 0} reference(s), ${result.written?.fields ?? 0} field(s)` +
            (failed ? ` — ${failed} failed, see the report` : ''),
        );
      }
    } catch (err) {
      console.error('stripReferenceNameMarkup failed:', err);
      const notDeployed = (err as { code?: string } | null)?.code === 'functions/not-found';
      toast.error(
        notDeployed
          ? 'The stripReferenceNameMarkup function is not deployed yet.'
          : err instanceof Error
            ? err.message
            : 'Failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  const busy = running !== null;
  const brandName = (id: string) => {
    const brand = brands.find((b) => b.id === id);
    return brand ? brand.displayName || brand.name : id;
  };
  const scopeLabel = (ids: string[]) =>
    ids.length === 0
      ? 'all brands'
      : ids.length <= 3
        ? ids.map(brandName).join(', ')
        : `${ids.length} selected brands`;
  const scanningLabel =
    brandIds.length === 0
      ? 'Scanning all brands…'
      : `Scanning ${brandIds.length} selected brand${brandIds.length === 1 ? '' : 's'}…`;

  return (
    <CollapsibleSection sectionId="stripReferenceNameMarkup" title="Strip markup from reference names">
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-600">
          Some shop feeds wrap product titles in theme tags, so imported references ended up with names like{' '}
          {code('<tc>ALLIANCE</tc>')}. New imports are cleaned on the way in; this fixes references already
          stored. It rewrites <strong>Model, Collection, Product Name, Series and Nickname</strong> only — tags
          removed, the text inside kept, entities such as {code('&amp;')} decoded. The reference number is never
          touched, and a field that is nothing but markup is reported, not blanked.
        </p>

        <div className="space-y-2">
          <BrandMultiSelect
            brands={brands}
            loading={brandsLoading}
            value={selectedBrandIds}
            onChange={setSelectedBrandIds}
            disabled={busy || allBrands}
            onFirstFocus={() => void loadBrands()}
          />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allBrands}
              disabled={busy}
              onChange={(e) => setAllBrands(e.target.checked)}
              className="rounded text-brand-600 focus:ring-brand-500"
            />
            Run against all brands
            <span className="text-xs text-gray-500">(scans every reference; the brand list above is ignored)</span>
          </label>
          <p className="text-xs text-gray-500" aria-live="polite">
            {running === 'dryRun' ? (
              <strong>{scanningLabel}</strong>
            ) : scopeChosen ? (
              <>
                Scope: <strong>{scopeLabel(brandIds)}</strong>. A dry run is required for whichever scope you
                choose.
              </>
            ) : (
              'Pick one or more brands, or tick “Run against all brands”.'
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={running === 'dryRun'}
            disabled={busy || !scopeChosen}
            title={scopeChosen ? undefined : 'Pick at least one brand, or tick “Run against all brands”'}
            onClick={() => void run('dryRun')}
          >
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'apply'}
            disabled={busy || !canApply}
            title={
              !report
                ? 'Run a dry run first'
                : !scopeMatchesReport
                  ? 'The brand selection has changed since this dry run — run it again'
                  : undefined
            }
            onClick={() => setConfirming(true)}
          >
            {canApply ? `Clean ${report!.affected} reference${report!.affected === 1 ? '' : 's'}…` : 'Clean…'}
          </Button>
        </div>

        {report && (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-gray-900">{report.mode === 'dryRun' ? 'Dry run' : 'After clean'}</span>
              <Badge variant={report.mode === 'dryRun' ? 'info' : 'success'}>
                {report.mode === 'dryRun' ? 'no writes' : 'written'}
              </Badge>
              <span className="text-xs text-gray-500">
                {scopeLabel(report.brandIds)} · {report.scanned.toLocaleString()} references scanned ·{' '}
                {report.affected.toLocaleString()} with markup · {report.fieldsToWrite.toLocaleString()} fields
              </span>
            </div>

            {report.written && (
              <p className="text-xs text-gray-700">
                Cleaned {report.written.references.toLocaleString()} reference(s),{' '}
                {report.written.fields.toLocaleString()} field(s). Run a dry run again to confirm nothing is left.
              </p>
            )}
            {(report.written?.failures.length ?? 0) > 0 && (
              <div role="alert" className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                <div className="font-semibold">{report.written!.failures.length} reference(s) failed to write</div>
                <ul className="mt-1 space-y-0.5">
                  {report.written!.failures.slice(0, 20).map((f) => (
                    <li key={f.path} className="break-all">
                      {f.path}: {f.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {report.byBrand.length > 0 && (
              <table className="text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="py-1 pr-6 text-left font-medium">Brand</th>
                    <th className="py-1 pr-6 text-right font-medium">References</th>
                    <th className="py-1 text-left font-medium">Fields</th>
                  </tr>
                </thead>
                <tbody>
                  {report.byBrand.map((b) => (
                    <tr key={b.brandId}>
                      <td className="py-0.5 pr-6 font-mono">{b.brandId}</td>
                      <td className="py-0.5 pr-6 text-right tabular-nums">{b.references.toLocaleString()}</td>
                      <td className="py-0.5 text-gray-600">
                        {Object.entries(b.fields)
                          .map(([field, n]) => `${field} ${n}`)
                          .join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {report.samples.length > 0 && (
              <details>
                <summary className="cursor-pointer text-xs text-gray-700">
                  {report.mode === 'dryRun' ? 'What will change' : 'What changed'} ({report.samples.length}
                  {report.samplesTruncated ? ` of ${report.affected}` : ''} references)
                </summary>
                <ul className="mt-2 space-y-2 text-xs">
                  {report.samples.map((s) => (
                    <li key={s.path}>
                      <div className="font-mono text-gray-800">
                        {s.brandId} / {s.reference || s.refId}
                        {s.isVariant ? ' · variant' : ''}
                      </div>
                      {s.changes.map((c) => (
                        <div key={c.field} className="pl-3 text-gray-600">
                          {c.field}: <span className="text-red-700 line-through">{c.before}</span>{' '}
                          <span className="text-green-700">{c.after}</span>
                        </div>
                      ))}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {report.emptied.length > 0 && (
              <p className="text-xs text-amber-800">
                {report.emptied.length} reference(s) have a name field that is nothing but markup. Those fields
                are left as they are — fix them by hand:{' '}
                {report.emptied.slice(0, 5).map((e) => e.path.split('/').slice(1).join('/')).join('; ')}
                {report.emptied.length > 5 ? '…' : ''}
              </p>
            )}
            {report.brandDocsWithMarkup.length > 0 && (
              <p className="text-xs text-amber-800">
                The brand document itself carries markup in:{' '}
                {report.brandDocsWithMarkup.map((b) => `${b.brandId} (${b.fields.join(', ')})`).join('; ')}. This
                tool does not change brand documents — edit those in the brand editor.
              </p>
            )}
          </div>
        )}
      </div>

      <Modal isOpen={confirming} onClose={() => setConfirming(false)} title="Clean reference names?" size="md">
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            This rewrites <strong>{report?.fieldsToWrite.toLocaleString()} name field(s)</strong> on{' '}
            <strong>{report?.affected.toLocaleString()} reference(s)</strong> in{' '}
            <strong>{report ? scopeLabel(report.brandIds) : ''}</strong>, exactly as listed in the dry run.
          </p>
          <p>
            Only Model, Collection, Product Name, Series and Nickname are written. There is no undo from here;
            the dry-run report is the record of the previous values.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={() => void run('apply')}>
              Clean {report?.affected.toLocaleString()} reference{report?.affected === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}
